import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Play, X } from "lucide-react";
import PageLayout from "../components/PageLayout";
import CtaBand from "../components/CtaBand";
import { Eyebrow, Reveal } from "../components/Reveal";
import { useContent } from "../content-context";

const SEO = {
  title: "Photo & Video Gallery — Island Life in Clew Bay | Collanmore Island Lodge",
  description:
    "Browse genuine photographs and videos of Collanmore Island Lodge: Clew Bay aerials, the lodge and accommodation, beach sauna, hot tub, kayaking, RIB adventures, celebrations and Traitors Island.",
  path: "/gallery/",
};

export default function GalleryPage() {
  const { galleryPage: gp } = useContent();
  const items = gp.items;
  const [filter, setFilter] = useState("All");
  const [open, setOpen] = useState(-1);

  const cats = ["All", ...new Set(items.map((i) => i.category))];
  const shown = filter === "All" ? items : items.filter((i) => i.category === filter);

  const close = useCallback(() => setOpen(-1), []);
  const step = useCallback(
    (dir) => setOpen((o) => (o < 0 ? o : (o + dir + shown.length) % shown.length)),
    [shown.length]
  );

  useEffect(() => {
    if (open < 0) return undefined;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open, close, step]);

  const current = open >= 0 ? shown[open] : null;

  return (
    <PageLayout seo={SEO}>
      <section className="relative overflow-hidden pt-28 md:pt-36">
        <div className="mx-auto max-w-[1440px] px-5 pb-14 md:px-10">
          <Reveal>
            <Eyebrow>{gp.heroEyebrow}</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-6 max-w-3xl font-display text-4xl font-medium leading-[1.05] text-navy md:text-6xl" data-testid="page-h1">
              {gp.heroH1}
            </h1>
          </Reveal>
          {gp.heroParas.map((p, i) => (
            <Reveal key={i} delay={0.14 + i * 0.06}>
              <p className="mt-5 max-w-2xl text-sm font-light leading-relaxed text-navy/75 md:text-base">{p}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-ivory pb-24 md:pb-32" data-testid="gallery-page-grid">
        <div className="mx-auto max-w-[1440px] px-5 md:px-10">
          <div className="flex flex-wrap gap-3" data-testid="gallery-filters">
            {cats.map((c) => (
              <button
                key={c}
                onClick={() => {
                  setFilter(c);
                  setOpen(-1);
                }}
                data-testid={`gallery-filter-${c === "All" ? "all" : c.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                className={`px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.18em] transition-colors duration-300 ${
                  filter === c ? "bg-navy text-ivory" : "border border-navy/20 text-navy/70 hover:border-gold hover:text-navy"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="mt-10 columns-1 gap-5 sm:columns-2 lg:columns-3" data-testid="gallery-masonry">
            {shown.map((item, i) => (
              <Reveal key={item.src + String(i)} delay={(i % 3) * 0.05} className="mb-5 break-inside-avoid">
                <button
                  onClick={() => setOpen(i)}
                  data-testid={`gallery-item-${i + 1}`}
                  aria-label={`Open: ${item.alt}`}
                  className="group relative block w-full overflow-hidden"
                >
                  {item.type === "video" ? (
                    <video src={item.src} muted playsInline preload="metadata" className="w-full object-cover" />
                  ) : (
                    <img src={item.src} alt={item.alt} loading="lazy" className="w-full object-cover" />
                  )}
                  {item.type === "video" && (
                    <span className="absolute inset-0 flex items-center justify-center">
                      <span className="flex h-16 w-16 items-center justify-center rounded-full bg-navy/80 text-gold ring-1 ring-gold">
                        <Play className="ml-1 h-6 w-6" />
                      </span>
                    </span>
                  )}
                  <span className="absolute inset-0 bg-navy/0 transition-colors duration-500 group-hover:bg-navy/30" />
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBand heading={gp.ctaHeading} lead={gp.ctaLead} cta={gp.ctaLabel} to="/contact/" />

      {current && (
        <div className="fixed inset-0 z-[100] flex flex-col bg-navy/95 backdrop-blur-sm" data-testid="gallery-lightbox" onClick={close}>
          <div className="flex items-center justify-between px-5 py-4 md:px-8" onClick={(e) => e.stopPropagation()}>
            <p className="text-xs uppercase tracking-widest2 text-ivory/70" data-testid="lightbox-counter">
              {open + 1} / {shown.length}
            </p>
            <button
              onClick={close}
              data-testid="lightbox-close"
              aria-label="Close the gallery viewer"
              className="flex h-11 w-11 items-center justify-center border border-ivory/25 text-ivory transition-colors hover:border-gold hover:text-gold"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="relative flex flex-1 items-center justify-center px-14 pb-8 md:px-24" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => step(-1)}
              data-testid="lightbox-prev"
              aria-label="Previous photograph"
              className="absolute left-3 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center border border-ivory/25 bg-navy/60 text-ivory transition-colors hover:border-gold hover:text-gold md:left-8"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            {current.type === "video" ? (
              <video src={current.src} controls autoPlay playsInline className="max-h-[68vh] w-auto max-w-full" data-testid="lightbox-video" />
            ) : (
              <img src={current.src} alt={current.alt} className="max-h-[68vh] w-auto max-w-full object-contain" data-testid="lightbox-image" />
            )}
            <button
              onClick={() => step(1)}
              data-testid="lightbox-next"
              aria-label="Next photograph"
              className="absolute right-3 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center border border-ivory/25 bg-navy/60 text-ivory transition-colors hover:border-gold hover:text-gold md:right-8"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </div>
          <p className="px-16 pb-8 text-center text-sm font-light text-ivory/70" onClick={(e) => e.stopPropagation()}>
            {current.alt}
          </p>
        </div>
      )}
    </PageLayout>
  );
}
